import React, { useEffect, useState } from "react";
import { Loader2, Eye, Users, Clock, CheckCircle2, BarChart3, Globe, TrendingUp, MousePointerClick, CalendarDays, Radio, History } from "lucide-react";
import { api, adminHeaders } from "../lib/api";

const fmtTime = (s) => {
  s = Math.round(s || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${sec}s`;
  return `${sec}s`;
};
const pct = (r) => `${Math.round((r || 0) * 100)}%`;
const PAGE_LABELS = {
  "/": "Home",
  "/search": "Search",
  "/watch": "Browse",
  "/series/:id": "Series page",
  "/watch/:id": "Video player",
};
const pageLabel = (p) => PAGE_LABELS[p] || p;

const VisitorChart = ({ data }) => {
  const rows = data || [];
  const max = Math.max(1, ...rows.map((d) => d.views));
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5 mb-10" data-testid="visitor-chart">
      <div className="flex items-center gap-2 text-blood mb-5"><TrendingUp size={18} /><span className="text-xs tracking-widest uppercase font-bold">Page Views — Last 30 Days</span></div>
      <div className="flex items-end gap-[3px] h-40">
        {rows.map((d) => (
          <div key={d.date} className="flex-1 group relative flex flex-col justify-end h-full" data-testid={`chart-bar-${d.date}`}>
            <div
              className="w-full bg-blood/70 group-hover:bg-blood rounded-t transition-colors"
              style={{ height: `${Math.max(2, (d.views / max) * 100)}%` }}
            />
            <div className="pointer-events-none absolute bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black border border-white/10 rounded px-2 py-1 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity z-10">
              {d.date}: {d.views} views · {d.visitors} visitors
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-3 text-[10px] text-white/40 tabular-nums">
        <span>{rows[0]?.date}</span>
        <span>{rows[rows.length - 1]?.date}</span>
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-blood mb-3"><Icon size={18} /><span className="text-xs tracking-widest uppercase font-bold">{label}</span></div>
    <p className="font-display font-black text-3xl text-white">{value}</p>
  </div>
);

const Table = ({ title, rows, cols }) => (
  <div className="mb-10">
    <h3 className="font-display font-bold text-xl text-white mb-4">{title}</h3>
    <div className="overflow-x-auto rounded-xl border border-white/10">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-white/5 text-white/50 text-left">
            {cols.map((c) => <th key={c.key} className={`px-4 py-3 font-semibold ${c.right ? "text-right" : ""}`}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={cols.length} className="px-4 py-6 text-center text-white/40">No data yet.</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-t border-white/5 hover:bg-white/5">
              {cols.map((c) => <td key={c.key} className={`px-4 py-3 ${c.right ? "text-right tabular-nums" : "text-white/90"}`}>{c.render ? c.render(r) : r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [visitors, setVisitors] = useState(null);
  const [live, setLive] = useState(null);
  const [views48, setViews48] = useState(null);
  const [views60, setViews60] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/summary", { headers: adminHeaders() }).then((r) => setData(r.data)).catch(() => {}),
      api.get("/analytics/visitors", { headers: adminHeaders() }).then((r) => setVisitors(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  // Live "Watching Now" + rolling "Views — Last 48 Hours" — poll every 5s.
  useEffect(() => {
    let active = true;
    const poll = () => {
      api.get("/analytics/watching-now", { headers: adminHeaders() })
        .then((r) => { if (active) setLive(r.data); }).catch(() => {});
      api.get("/analytics/views-48h", { headers: adminHeaders() })
        .then((r) => { if (active) setViews48(r.data); }).catch(() => {});
      api.get("/analytics/views-60m", { headers: adminHeaders() })
        .then((r) => { if (active) setViews60(r.data); }).catch(() => {});
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => { active = false; clearInterval(t); };
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blood" size={36} /></div>;
  if (!data) return <p className="text-white/40">Could not load analytics.</p>;

  const o = data.overall;
  const v = visitors;
  return (
    <div data-testid="analytics-panel">
      <div className="mb-14" data-testid="watching-now">
        <div className="flex items-center gap-2 mb-6">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blood opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-blood" />
          </span>
          <h2 className="font-display font-bold text-2xl text-white flex items-center gap-2"><Radio size={22} className="text-blood" /> Watching Now</h2>
        </div>
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 mb-4 flex items-baseline gap-4">
          <p className="font-display font-black text-6xl text-blood glow-red tabular-nums" data-testid="watching-now-total">{live ? live.total : 0}</p>
          <p className="text-white/50 text-sm uppercase tracking-widest font-bold">{(live && live.total === 1) ? "viewer watching" : "viewers watching"}</p>
        </div>
        <div className="rounded-xl border border-white/10 overflow-hidden">
          {(!live || live.by_episode.length === 0) ? (
            <p className="px-4 py-6 text-center text-white/40 text-sm" data-testid="watching-now-empty">No one is watching right now.</p>
          ) : (
            live.by_episode.map((r) => (
              <div key={r.episode_id} className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5" data-testid={`watching-row-${r.episode_id}`}>
                <span className="text-white/90 text-sm">
                  <span className="text-white/50">{r.series_title?.trim()}</span>
                  {r.season != null && <span className="text-blood font-bold mx-2">S{r.season}E{r.episode}</span>}
                  {r.title?.trim()}
                </span>
                <span className="flex items-center gap-2 text-white font-bold tabular-nums text-sm">
                  <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blood opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-blood" /></span>
                  {r.count} watching
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="mb-14" data-testid="views-60m">
        <h2 className="font-display font-bold text-2xl text-white mb-6 flex items-center gap-2"><Clock size={22} className="text-blood" /> Views — Last 60 Minutes</h2>
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 mb-4 flex items-baseline gap-4">
          <p className="font-display font-black text-6xl text-white tabular-nums" data-testid="views-60m-total">{views60 ? views60.total : 0}</p>
          <p className="text-white/50 text-sm uppercase tracking-widest font-bold">{(views60 && views60.total === 1) ? "view (rolling 60m)" : "views (rolling 60m)"}</p>
        </div>
        <div className="rounded-xl border border-white/10 overflow-hidden">
          {(!views60 || views60.by_episode.length === 0) ? (
            <p className="px-4 py-6 text-center text-white/40 text-sm" data-testid="views-60m-empty">No views in the last 60 minutes yet.</p>
          ) : (
            views60.by_episode.map((r) => (
              <div key={r.episode_id} className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5" data-testid={`views-60m-row-${r.episode_id}`}>
                <span className="text-white/90 text-sm">
                  <span className="text-white/50">{r.series_title?.trim()}</span>
                  {r.season != null && <span className="text-blood font-bold mx-2">S{r.season}E{r.episode}</span>}
                  {r.title?.trim()}
                </span>
                <span className="flex items-center gap-2 text-white font-bold tabular-nums text-sm">
                  <Eye size={14} className="text-blood" /> {r.count} {r.count === 1 ? "view" : "views"}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="mb-14" data-testid="views-48h">
        <h2 className="font-display font-bold text-2xl text-white mb-6 flex items-center gap-2"><History size={22} className="text-blood" /> Views — Last 48 Hours</h2>
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 mb-4 flex items-baseline gap-4">
          <p className="font-display font-black text-6xl text-white tabular-nums" data-testid="views-48h-total">{views48 ? views48.total : 0}</p>
          <p className="text-white/50 text-sm uppercase tracking-widest font-bold">{(views48 && views48.total === 1) ? "view (rolling 48h)" : "views (rolling 48h)"}</p>
        </div>
        <div className="rounded-xl border border-white/10 overflow-hidden">
          {(!views48 || views48.by_episode.length === 0) ? (
            <p className="px-4 py-6 text-center text-white/40 text-sm" data-testid="views-48h-empty">No views in the last 48 hours yet.</p>
          ) : (
            views48.by_episode.map((r) => (
              <div key={r.episode_id} className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5" data-testid={`views-48h-row-${r.episode_id}`}>
                <span className="text-white/90 text-sm">
                  <span className="text-white/50">{r.series_title?.trim()}</span>
                  {r.season != null && <span className="text-blood font-bold mx-2">S{r.season}E{r.episode}</span>}
                  {r.title?.trim()}
                </span>
                <span className="flex items-center gap-2 text-white font-bold tabular-nums text-sm">
                  <Eye size={14} className="text-blood" /> {r.count} {r.count === 1 ? "view" : "views"}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {v && (
        <div className="mb-14" data-testid="visitor-analytics">
          <h2 className="font-display font-bold text-2xl text-white mb-6 flex items-center gap-2"><Globe size={22} className="text-blood" /> Website Visitors</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Stat icon={Users} label="Unique Visitors" value={v.unique_visitors} testId="stat-unique-visitors-site" />
            <Stat icon={Globe} label="Total Visits" value={v.total_visits} testId="stat-total-visits" />
            <Stat icon={MousePointerClick} label="Page Views" value={v.page_views} testId="stat-page-views" />
            <Stat icon={CalendarDays} label="Visitors Today" value={v.visitors_today} testId="stat-visitors-today" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Stat icon={CalendarDays} label="Visitors · 7 Days" value={v.visitors_7d} testId="stat-visitors-7d" />
            <Stat icon={CalendarDays} label="Visitors · 30 Days" value={v.visitors_30d} testId="stat-visitors-30d" />
          </div>
          <VisitorChart data={v.chart} />
          <Table
            title="Most Visited Pages"
            rows={v.top_pages}
            cols={[
              { key: "path", label: "Page", render: (r) => <span><span className="text-white/90">{pageLabel(r.path)}</span> <span className="text-white/40 text-xs ml-2">{r.path}</span></span> },
              { key: "views", label: "Views", right: true },
            ]}
          />
        </div>
      )}

      <h2 className="font-display font-bold text-2xl text-white mb-6 flex items-center gap-2"><BarChart3 size={22} className="text-blood" /> Episode Analytics</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat icon={Eye} label="Total Plays" value={o.total_plays} testId="stat-total-plays" />
        <Stat icon={Users} label="Unique Viewers" value={o.unique_viewers} testId="stat-unique-viewers" />
        <Stat icon={Clock} label="Watch Time" value={fmtTime(o.total_watch_seconds)} testId="stat-watch-time" />
        <Stat icon={CheckCircle2} label="Completion" value={pct(o.completion_rate)} testId="stat-completion" />
      </div>

      <Table
        title="By Series"
        rows={data.by_series}
        cols={[
          { key: "title", label: "Series", render: (r) => r.title?.trim() },
          { key: "plays", label: "Plays", right: true },
          { key: "unique_viewers", label: "Unique", right: true },
          { key: "watch", label: "Watch Time", right: true, render: (r) => fmtTime(r.watch_seconds) },
          { key: "completion_rate", label: "Completion", right: true, render: (r) => pct(r.completion_rate) },
        ]}
      />

      <Table
        title="By Episode"
        rows={data.by_episode}
        cols={[
          { key: "ep", label: "Episode", render: (r) => <span><span className="text-blood font-bold mr-2">S{r.season}E{r.episode}</span>{r.title?.trim()}</span> },
          { key: "series_title", label: "Series", render: (r) => <span className="text-white/50">{r.series_title?.trim()}</span> },
          { key: "plays", label: "Plays", right: true },
          { key: "unique_viewers", label: "Unique", right: true },
          { key: "watch", label: "Watch Time", right: true, render: (r) => fmtTime(r.watch_seconds) },
          { key: "completion_rate", label: "Completion", right: true, render: (r) => pct(r.completion_rate) },
        ]}
      />
    </div>
  );
}
